import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/store/store';
import {
  fetchTickets,
  resetTickets,
  initialFilters,
} from '../../entities/ticket/model';
import { TicketFilters } from '../../types/ticketInterfaces';
import { TicketsTable } from '../../widgets/tickets-table';
import { TicketsFilters } from '../../features/tickets-filters/ui/TicketsFilters';
import { TicketCreateModal } from '../../features/ticket-create-modal/ui/TicketCreateModal';
import MainLayout from '@shared/ui/main-layout';
import { Tabs } from '../../shared/ui/tabs';
import { SearchInput } from '@shared/ui/search-input';
import { useHdPermissions } from '@shared/lib/ability';
import { helpdeskSettingsApi } from '../../api/services/helpdesk-settings.service';
import type { Branch } from '../../types/helpdeskSettingsInterfaces';

const Helpdesk: React.FC = () => {
  const dispatch = useAppDispatch();
  const hd = useHdPermissions();
  const { tickets, totalCount, listStatus } = useAppSelector(
    (state) => state.tickets,
  );

  const [filters, setFilters] = useState<TicketFilters>(initialFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSizeState] = useState<number>(() => {
    try {
      const saved = Number(localStorage.getItem('hd.pageSize'));
      return [10, 25, 50, 100].includes(saved) ? saved : 100;
    } catch {
      return 100;
    }
  });
  /** Смена размера страницы: запоминаем на этом устройстве и возвращаемся на первую страницу */
  const setPageSize = useCallback((n: number) => {
    try {
      localStorage.setItem('hd.pageSize', String(n));
    } catch {
      /* приватный режим и т.п. — просто не запоминаем */
    }
    setPageSizeState(n);
    setCurrentPage(1);
  }, []);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  // Филиалы — для переключателя «мои / все / конкретный» (только HD-staff).
  const [branches, setBranches] = useState<Branch[]>([]);
  useEffect(() => {
    if (!hd.isHdStaff) return;
    helpdeskSettingsApi
      .getBranches()
      .then((res) => setBranches(res.data.filter((b) => b.is_active)))
      .catch(() => setBranches([]));
  }, [hd.isHdStaff]);

  const handleBranchChange = useCallback(
    (value: string) => {
      const updated = { ...filters, branch: value };
      setFilters(updated);
      setCurrentPage(1);
      dispatch(fetchTickets({ filters: updated, page: 1, limit: pageSize }));
    },
    [dispatch, filters, pageSize],
  );

  /** Быстрый фильтр «Ждут ответа»: только заявки, где последнее слово за клиентом,
      самые давние — первыми. */
  const toggleAwaiting = useCallback(() => {
    const on = !filters.awaiting_reply;
    const updated = {
      ...filters,
      awaiting_reply: on,
      ordering: on ? 'last_client_message_at' : '-created_at',
    };
    setFilters(updated);
    setCurrentPage(1);
    dispatch(fetchTickets({ filters: updated, page: 1, limit: pageSize }));
  }, [dispatch, filters, pageSize]);

  /* ── Загрузка при изменении страницы ── */
  const loadTickets = useCallback(() => {
    dispatch(fetchTickets({ filters, page: currentPage, limit: pageSize }));
  }, [dispatch, filters, currentPage, pageSize]);

  useEffect(() => {
    loadTickets();
    return () => {
      dispatch(resetTickets());
    };
  }, [currentPage, pageSize]);

  /* ── Применить фильтры ── */
  const handleApplyFilters = useCallback(() => {
    setCurrentPage(1);
    dispatch(fetchTickets({ filters, page: 1, limit: pageSize }));
  }, [dispatch, filters, pageSize]);

  /* ── Сбросить фильтры ── */
  const handleResetFilters = useCallback(() => {
    setFilters(initialFilters);
    setCurrentPage(1);
    dispatch(
      fetchTickets({ filters: initialFilters, page: 1, limit: pageSize }),
    );
  }, [dispatch, pageSize]);

  /* ── Поиск ── */
  const handleSearch = useCallback(
    (value: string) => {
      const updated = { ...filters, search: value };
      setFilters(updated);
      if (value === '' || value.length >= 2) {
        setCurrentPage(1);
        dispatch(fetchTickets({ filters: updated, page: 1, limit: pageSize }));
      }
    },
    [dispatch, filters, pageSize],
  );

  const isFiltersApplied = useMemo(
    () => JSON.stringify(filters) !== JSON.stringify(initialFilters),
    [filters],
  );

  function filtersBar() {
    return (
      <div className="w-full flex items-center gap-[10px] mt-4">
        <SearchInput
          value={filters.search}
          onChange={handleSearch}
          placeholder="Поиск по номеру или теме..."
          className="max-w-[400px] w-full"
        />
        {/* Филиал: по умолчанию бэк показывает заявки филиалов оператора */}
        {hd.isHdStaff && branches.length > 1 && (
          <select
            value={filters.branch}
            onChange={(e) => handleBranchChange(e.target.value)}
            className="px-3 h-[40px] text-sm border border-[#D0D3D8] rounded-[10px] bg-white text-[#6B6F7B] focus:outline-none focus:ring-2 focus:ring-red-400"
            title="Филиал"
          >
            <option value="">Мои филиалы</option>
            <option value="all">Все филиалы</option>
            {branches.map((b) => (
              <option key={b.id} value={String(b.id)}>
                {b.name}
              </option>
            ))}
          </select>
        )}
        {hd.isHdStaff && (
          <button
            type="button"
            onClick={toggleAwaiting}
            title="Заявки, где последнее сообщение от клиента и оператор ещё не ответил"
            className={[
              'flex items-center gap-1.5 px-4 h-[40px] text-sm font-medium rounded-[10px] border transition-colors whitespace-nowrap',
              filters.awaiting_reply
                ? 'border-[#e3282a] text-[#e3282a] bg-[#e3282a]/5'
                : 'border-[#D0D3D8] text-[#6B6F7B] hover:border-[#9DA7B5]',
            ].join(' ')}
          >
            <span className="w-2 h-2 rounded-full bg-[#e3282a]" />
            Ждут ответа
          </button>
        )}
        {/* Расширенные фильтры — только для HD-staff (операторы видят больше критериев) */}
        {hd.isHdStaff && (
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className={[
              'flex items-center gap-1.5 px-4 h-[40px] text-sm font-medium rounded-[10px] border transition-colors whitespace-nowrap',
              showFilters
                ? 'border-[#e3282a] text-[#e3282a] bg-[#e3282a]/5'
                : 'border-[#D0D3D8] text-[#6B6F7B] hover:border-[#9DA7B5]',
            ].join(' ')}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            Фильтры
            {isFiltersApplied && (
              <span className="w-2 h-2 rounded-full bg-[#e3282a]" />
            )}
          </button>
        )}
        <div className="ml-auto">
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-5 h-[40px] text-sm font-semibold text-white bg-[#E3282A] rounded-[10px] hover:bg-[#C91F21] active:bg-[#A81B1D] transition-colors whitespace-nowrap"
          >
            <span className="text-lg leading-none">+</span>
            Новая заявка
          </button>
        </div>
      </div>
    );
  }

  return (
    <MainLayout>
      <MainLayout.Title>Helpdesk</MainLayout.Title>
      <MainLayout.Main>
        <Tabs variant="underline" contentAfterTabs={filtersBar}>
          <div title="Все заявки">
            {/* ── Панель фильтров ── */}
            {showFilters && (
              <TicketsFilters
                filters={filters}
                setFilters={setFilters}
                onApply={handleApplyFilters}
                onReset={handleResetFilters}
                isFiltersApplied={isFiltersApplied}
                pageSize={pageSize}
                setPageSize={setPageSize}
              />
            )}

            {/* ── Таблица ── */}
            <TicketsTable
              tickets={tickets}
              totalCount={totalCount}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              isLoading={listStatus === 'loading'}
              onColumnFilter={(field, value) => {
                const updated = { ...filters, [field]: value };
                setFilters(updated);
                setCurrentPage(1);
                dispatch(
                  fetchTickets({
                    filters: updated,
                    page: 1,
                    limit: pageSize,
                  }),
                );
              }}
            />
          </div>
        </Tabs>
      </MainLayout.Main>

      {/* ── Модальное окно создания ── */}
      {showCreateModal && (
        <TicketCreateModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            handleApplyFilters();
          }}
        />
      )}
    </MainLayout>
  );
};

export default Helpdesk;
